"use client";
import { useTranslations } from "next-intl";
import { MediaStorage } from "@shared/utils";
import { Logger } from "@shared/utils/logger";
import { toastNotify } from "@client/components/ui/toast";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  VideoPresets,
  LocalVideoTrack,
  LocalAudioTrack,
  createLocalVideoTrack,
  createLocalAudioTrack
} from "livekit-client";

export interface UsePreJoinMediaOptions {
  initialVideoEnabled?: boolean;
  initialAudioEnabled?: boolean;
  videoDeviceId?: string;
  audioDeviceId?: string;
}

export interface UsePreJoinMediaReturn {
  videoTrack: LocalVideoTrack | undefined;
  audioTrack: LocalAudioTrack | undefined;
  videoEnabled: boolean;
  audioEnabled: boolean;
  setVideoEnabled: (enabled: boolean) => void;
  setAudioEnabled: (enabled: boolean) => void;
  toggleVideo: () => void;
  toggleAudio: () => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

/**
 * Hook for managing pre-join media tracks (video/audio).
 * Handles track creation, cleanup, and device changes.
 */
export const usePreJoinMedia = ({
  initialVideoEnabled = true,
  initialAudioEnabled = true,
  videoDeviceId = "",
  audioDeviceId = ""
}: UsePreJoinMediaOptions = {}): UsePreJoinMediaReturn => {
  const t = useTranslations();

  // Use refs to track initial values - only use them on first mount
  const isInitialMount = useRef(true);
  const initialVideoRef = useRef(initialVideoEnabled);
  const initialAudioRef = useRef(initialAudioEnabled);

  // Initialize state with the initial values captured at first mount
  const [videoEnabled, setVideoEnabled] = useState(() => initialVideoRef.current);
  const [audioEnabled, setAudioEnabled] = useState(() => initialAudioRef.current);
  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack | undefined>(undefined);
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack | undefined>(undefined);

  // Mark initial mount as complete after first render
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  // Refs for cleanup on unmount
  const videoTrackRef = useRef<LocalVideoTrack | undefined>(undefined);
  const audioTrackRef = useRef<LocalAudioTrack | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Keep refs in sync
  useEffect(() => {
    videoTrackRef.current = videoTrack;
  }, [videoTrack]);

  useEffect(() => {
    audioTrackRef.current = audioTrack;
  }, [audioTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      videoTrackRef.current?.stop();
      audioTrackRef.current?.stop();
    };
  }, []);

  // Create/update video track
  useEffect(() => {
    let track: LocalVideoTrack | undefined;
    let isCancelled = false;

    const createTrack = async () => {
      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
        videoTrackRef.current = undefined;
      }

      if (videoEnabled) {
        try {
          await new Promise((resolve) => setTimeout(resolve, 100));
          if (isCancelled) return;

          const newTrack = await createLocalVideoTrack({
            deviceId: videoDeviceId || undefined,
            resolution: VideoPresets.h720
          });

          if (isCancelled) {
            newTrack.stop();
            return;
          }

          track = newTrack;
          setVideoTrack(track);
        } catch (e) {
          if (isCancelled) return;
          Logger.error("usePreJoinMedia", "Error creating video track", e);
          toastNotify.error(t("toast.media.cameraAccessError"));
          setVideoEnabled(false);
        }
      } else {
        setVideoTrack(undefined);
      }
    };

    createTrack();

    return () => {
      isCancelled = true;
      track?.stop();
    };
  }, [videoEnabled, videoDeviceId, t]);

  // Create/update audio track
  useEffect(() => {
    let track: LocalAudioTrack | undefined;
    let isCancelled = false;

    const createTrack = async () => {
      if (audioEnabled) {
        try {
          const newTrack = await createLocalAudioTrack({
            deviceId: audioDeviceId || undefined
          });

          if (isCancelled) {
            newTrack.stop();
            return;
          }

          track = newTrack;
          setAudioTrack(track);
        } catch (e) {
          if (isCancelled) return;
          Logger.error("usePreJoinMedia", "Error creating audio track", e);
          toastNotify.error(t("toast.media.micAccessError"));
          setAudioEnabled(false);
        }
      } else {
        setAudioTrack(undefined);
      }
    };

    createTrack();

    return () => {
      isCancelled = true;
      track?.stop();
    };
  }, [audioEnabled, audioDeviceId, t]);

  // Attach video to element
  useEffect(() => {
    if (videoTrack && videoRef.current) {
      videoTrack.attach(videoRef.current);
    }
    return () => {
      videoTrack?.detach();
    };
  }, [videoTrack]);

  const toggleVideo = useCallback(() => {
    setVideoEnabled((prev) => {
      MediaStorage.savePreferences(audioEnabled, !prev);
      return !prev;
    });
  }, [audioEnabled]);

  const toggleAudio = useCallback(() => {
    setAudioEnabled((prev) => {
      MediaStorage.savePreferences(!prev, videoEnabled);
      return !prev;
    });
  }, [videoEnabled]);

  return {
    videoTrack,
    audioTrack,
    videoEnabled,
    audioEnabled,
    setVideoEnabled,
    setAudioEnabled,
    toggleVideo,
    toggleAudio,
    videoRef
  };
};
